<template>
  <component
    :is="instance.getFormName()"
    v-bind="{
      dataRefid: 'documentFormName',
      focusOnLoad: focusNameOnLoad,
      instance,
      label: DOCUMENT_FIELD_LABEL[DOCUMENT_FIELD.NAME],
      readonly,
    }"
  />
  <component
    :is="instance.getFormTagsField()"
    v-if="isSavedDocument"
    v-bind="{
      dataRefid: 'documentFormTags',
      instance,
      readonly,
    }"
  />
  <UIDocumentLink
    v-if="isDocumentLinkDisplayed"
    :document-id="instance.id"
    :name="instance.field<string>(DOCUMENT_FIELD.NAME).value"
    :path="instance.field<string>(DOCUMENT_FIELD.PATH).value"
    :source="instance.field<DocumentSource>(DOCUMENT_FIELD.SOURCE).value"
    size="sm"
    fill="outlined"
    class="w-full"
    :editable="!instance.isFile"
    @edit="editMode = true"
  />
  <component
    :is="instance.getFormThumbnail()"
    v-else-if="instance.isThumbnail"
    v-bind="{
      dataRefid: 'documentFormThumbnail',
      instance,
      label: DOCUMENT_FIELD_LABEL[DOCUMENT_FIELD.FILENAME],
      readonly,
    }"
  />
  <component
    :is="instance.getFormFile()"
    v-else-if="instance.isFile"
    v-bind="{
      dataRefid: 'documentFormFilename',
      focusOnLoad: focusFilenameOnLoad,
      instance,
      label: DOCUMENT_FIELD_LABEL[DOCUMENT_FIELD.FILENAME],
      readonly,
    }"
  />
  <component
    :is="instance.getFormPath()"
    v-else
    v-bind="{
      dataRefid: 'documentFormPath',
      focusOnLoad: focusPathOnLoad,
      instance,
      label: DOCUMENT_FIELD_LABEL[DOCUMENT_FIELD.PATH],
      readonly,
    }"
  />
  <component
    :is="instance.getFormAssetId()"
    v-bind="{
      dataRefid: 'documentFormAsset',
      focusOnLoad: focusAssetOnLoad,
      instance,
      label: DOCUMENT_FIELD_LABEL[DOCUMENT_FIELD.ASSET_ID],
      readonly,
    }"
  />
  <UIButtonsGroup>
    <template #default="{ short }">
      <UIButtonClose :short @click="handleClose" />
      <UIButtonSave
        v-if="!readonly"
        ref="buttonSaveRef"
        data-refid="documentFormSave"
        :disabled
        :loading
        :short
        @click="handleSave"
      />
    </template>
  </UIButtonsGroup>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, provide, ref, useTemplateRef } from 'vue'

import { DocumentSource, ReadonlyMode } from '@types'
import { DocumentClass } from '..'
import { EntityState } from '@/entities/utils/enums'

import {
  DOCUMENT_FIELD,
  DOCUMENT_FIELD_LABEL,
  DOCUMENT_ID_PREFIX,
} from '../utils/const'
import { ROUTE_NAME } from '@/const'
import { NOTIFICATION_DELAY, READONLY_MODE } from '@/const/common'

import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIButtonClose, UIButtonSave, UIButtonsGroup } from '@ui/buttons'
import { UIDocumentLink } from '@ui/core'

type Props = {
  instance: DocumentClass
}

type Emits = {
  close: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { progress, remove, update } = useNotifications()

const assetsBunchStore = useAssetsBunchStore()

const loading = ref(false)
const editMode = ref(false)

const buttonSaveRef = useTemplateRef('buttonSaveRef')
provide('document-save-focus', async () => {
  await nextTick()
  buttonSaveRef.value?.focus()
})

const readonly = inject<ReadonlyMode>(READONLY_MODE)

const disabled = computed(
  () => ![EntityState.CHANGED, EntityState.NEW].includes(props.instance.state),
)

const isSavedDocument = computed(() =>
  props.instance.id.startsWith(DOCUMENT_ID_PREFIX),
)
provide('isSavedDocument', isSavedDocument)

const focusNameOnLoad = computed(
  () =>
    !isSavedDocument.value && !props.instance.field(DOCUMENT_FIELD.NAME).value,
)

const focusPathOnLoad = computed(
  () =>
    !isSavedDocument.value &&
    !focusNameOnLoad.value &&
    !props.instance.field(DOCUMENT_FIELD.PATH).value,
)

const focusFilenameOnLoad = computed(() => focusPathOnLoad.value)

const focusAssetOnLoad = computed(
  () =>
    !isSavedDocument.value &&
    !focusNameOnLoad.value &&
    !focusPathOnLoad.value &&
    !props.instance.field(DOCUMENT_FIELD.ASSET_ID).value,
)

const isDocumentLinkDisplayed = computed(() => !editMode.value && !props.instance.isNew)

const handleClose = () => {
  emit('close')
}

const handleSave = async () => {
  loading.value = true
  const isNew = props.instance.isNew
  const nid = await progress({
    message: `${isNew ? 'Creating' : 'Updating'} document`,
  })
  try {
    if (isNew) {
      await props.instance.store()
      if (props.instance.isThumbnail) {
        const assetId = props.instance.field<string>(DOCUMENT_FIELD.ASSET_ID).value
        const asset = await assetsBunchStore.getElementById(assetId)
        asset && (asset.thumbnailId = props.instance.id)
      }
    } else {
      await props.instance.update()
    }
    await update(
      nid,
      {
        type: 'success',
        message: `Document ${props.instance.field(DOCUMENT_FIELD.NAME).value} ${
          isNew ? 'created' : 'updated'
        }`,
        link: {
          text: 'Open',
          to: {
            name: ROUTE_NAME.DOCUMENTS_ITEM,
            params: { id: props.instance.id },
          },
        },
      },
      NOTIFICATION_DELAY,
    )
    emit('close')
  } catch (e) {
    await remove(nid)
    handleCatchedError(e as string, props.instance.get())
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  if (props.instance.isNew) return
  props.instance.isFile && (await props.instance.fetch())
})
</script>
